import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  BrowserRouter, Routes, Route, useLocation, useNavigate,
} from 'react-router';
import { ThemeProvider } from '../shared/theme';
import { ToastHost, notifyError } from '../shared/toast';
import { tenantSlugFromHostname } from '../shared/validators';
import { clampAccent } from '../shared/branding';
import { getPublicBranding } from '../shared/services/institutions';
import api from '../shared/services/api';
import { buildPortalAuthUrl, buildPortalDirectoryUrl, signout } from '../shared/services/auth';
import PortalShell from './PortalShell';
import PortalHome from './pages/PortalHome';
import OutageState from './pages/OutageState';
import LoadingState from './pages/LoadingState';
import AuthHop from './pages/AuthHop';
import MagicLinkVerify from './pages/MagicLinkVerify.jsx';
import AcceptInvite from './pages/AcceptInvite';
import { pendingInviteResumePath } from './pendingInvite.js';
import {
  clearDevPortalAccessToken,
  consumeDevPortalAccessToken,
} from '../shared/devPortalSession.js';
import {
  buildPortalPath,
  readPortalConversationId,
  readPortalView,
} from './portalUrl.js';
import { usePortalDocumentTitle } from './usePortalDocumentTitle.js';
import AdminMfaDialog from '../console/components/AdminMfaDialog.jsx';
import { openAdminMfa } from '../shared/services/adminMfaGate.js';

/**
 * Redirect www / bare-apex hits away from the portal build. The portal is only
 * ever served on a school subdomain (`<slug>.coffeechats.me`); `www` and the
 * bare apex belong to the marketing site. Runs before any branding fetch so an
 * apex visitor never sees a school shell. No-op off the production apex (dev /
 * localhost / preview hosts), where slug resolution + VITE_TENANT_OVERRIDE win.
 * @returns {boolean} true when a redirect was issued (render nothing)
 */
function maybeRedirectApex() {
  if (typeof window === 'undefined') { return false; }
  const host = window.location.hostname.toLowerCase();
  if (!host.endsWith('coffeechats.me')) { return false; }
  const labels = host.split('.');
  // `www.coffeechats.me` (first label www) or the bare apex `coffeechats.me`
  // (two labels, no subdomain) - marketing.
  if (labels[0] === 'www' || labels.length === 2) {
    window.location.replace('https://coffeechats.me');
    return true;
  }
  return false;
}

/**
 * The tenant-resolving index route. Drives the FIVE-STATE public-branding
 * contract and, on success, mounts the authed shell.
 */
function PortalRoot() {
  const location = useLocation();
  const navigate = useNavigate();
  useMemo(() => {
    if (import.meta.env.DEV) { consumeDevPortalAccessToken(); }
    return true;
  }, []);
  const redirecting = useMemo(() => { return maybeRedirectApex(); }, []);
  const slug = useMemo(() => { return tenantSlugFromHostname(); }, []);
  // 'loading' | 'ready' | 'find_school' | 'outage'
  const [state, setState] = useState(() => { return slug ? 'loading' : 'find_school'; });
  const [branding, setBranding] = useState(null);
  const [profile, setProfile] = useState(null);
  const activeView = readPortalView(location.pathname);
  const activeConversationId = readPortalConversationId(location.pathname);
  const [signingOut, setSigningOut] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [portalAuthed, setPortalAuthed] = useState(false);
  const [portalRole, setPortalRole] = useState(null);
  const [portalContext, setPortalContext] = useState(null);
  const [programStatus, setProgramStatus] = useState(null);
  const [inviteResumePath] = useState(() => { return pendingInviteResumePath(); });
  const documentSchoolName = state === 'ready'
    ? branding?.branding?.displayName || branding?.name
    : null;
  usePortalDocumentTitle(documentSchoolName);

  const load = useCallback(async () => {
    // No tenant (localhost with no override, or a reserved subdomain): honor it
    // as no-tenant (hops to the generic sign-in at /auth) rather than a fetch.
    if (!slug) {
      setState('find_school');
      return;
    }
    setState('loading');
    try {
      const data = await getPublicBranding(slug, { fresh: true });
      setBranding(data);
      setState('ready');
      api.get('/user')
        .then((response) => { setProfile(response.data || null); })
        .catch(() => { setProfile(null); });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // reason 'unknown_slug' (or any other 404): the school isn't here.
        setState('find_school');
      } else {
        // 5xx / 429 / network drop (no response). NEVER find_school. An
        // outage must not read as a deleted account.
        setState('outage');
      }
    }
  }, [slug]);

  useEffect(() => {
    if (redirecting || inviteResumePath) { return; }
    load();
  }, [redirecting, inviteResumePath, load]);

  useEffect(() => {
    if (inviteResumePath) { window.location.replace(inviteResumePath); }
  }, [inviteResumePath]);

  // No tenant known (bare portal host, or an unknown/typo'd subdomain): hop to
  // the shared /auth entry point for a generic sign-in instead of ever
  // rendering a directory of every partnered school.
  //
  // Only the typo'd-subdomain path earns an explanation. Landing here with no
  // slug at all (bare portal host, reserved subdomain) is a normal front door;
  // telling those visitors we couldn't find their school would invent a failure
  // they never hit. The notice also tells /auth to ignore the referrer handoff,
  // which would otherwise read this dead slug as a real school (see AuthHop).
  useEffect(() => {
    if (state !== 'find_school') { return; }
    window.location.replace(buildPortalAuthUrl(slug ? { notice: 'unknown_school' } : {}));
  }, [state, slug]);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await signout();
    } catch {
      setSigningOut(false);
      notifyError('Could not sign out. Please try again.');
      return;
    }
    if (import.meta.env.DEV) { clearDevPortalAccessToken(); }
    setSignedOut(true);
    setPortalAuthed(false);
    setPortalRole(null);
    setPortalContext(null);
    setProfile(null);
    setSigningOut(false);
    window.location.replace(buildPortalDirectoryUrl());
  }, []);

  const handlePortalAuthChange = useCallback((authed, context) => {
    const isAuthed = Boolean(authed);
    setPortalAuthed(isAuthed);
    setPortalContext(isAuthed ? context || null : null);
    setPortalRole(isAuthed
      ? (context?.superAdmin ? 'SUPER_ADMIN' : context?.institutionAdmin?.role || null)
      : null);
  }, []);

  const handleNavigate = useCallback((view) => {
    navigate(buildPortalPath(view));
  }, [navigate]);

  const handleAiConversationChange = useCallback((conversationId, options = {}) => {
    navigate(buildPortalPath('ai', conversationId), { replace: options.replace === true });
  }, [navigate]);

  useEffect(() => {
    if (state !== 'ready') { return; }
    const canonicalPath = buildPortalPath(activeView, activeConversationId);
    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [activeConversationId, activeView, location.pathname, navigate, state]);

  if (redirecting) { return null; }
  if (inviteResumePath) { return null; }
  if (state === 'loading' || state === 'find_school') { return <LoadingState />; }
  if (state === 'outage') { return <OutageState onRetry={load} />; }

  // ready
  const b = branding?.branding || {};
  const rawAccent = b.accentColor || null;
  const accent = rawAccent ? clampAccent(rawAccent) : null;
  return (
    <PortalShell
      name={branding?.name}
      slug={branding?.slug || slug}
      displayName={b.displayName || branding?.name}
      accent={accent}
      logoFileId={b.logoFileId || null}
      logoSource={b.logoSource}
      autoLogoDomain={b.autoLogoDomain}
      activeView={activeView}
      onNavigate={handleNavigate}
      profile={profile}
      role={portalRole}
      accessContext={portalContext}
      onSignOut={handleSignOut}
      onTwoFactor={portalRole ? openAdminMfa : undefined}
      signingOut={signingOut}
      showSignOut={portalAuthed && !signedOut}
      chromeHidden={!portalAuthed || signedOut}
      programStatus={programStatus}
    >
      <PortalHome
        slug={branding?.slug || slug}
        branding={branding}
        accent={accent}
        profile={profile}
        activeView={activeView}
        aiConversationId={activeConversationId}
        onAiConversationChange={handleAiConversationChange}
        onNavigate={handleNavigate}
        onBrandingChange={setBranding}
        forceSignedOut={signedOut}
        onAuthChange={handlePortalAuthChange}
        onProgramStatusChange={setProgramStatus}
      />
    </PortalShell>
  );
}

export default function App() {
  return (
    <ThemeProvider theme="portal">
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* Mounted at the portal root so any institution-admin request can
            trigger enrollment/challenge and be replayed by the shared API
            interceptor after elevation. */}
        <AdminMfaDialog />
        <ToastHost />
        <Routes>
          <Route path="/auth" element={<AuthHop />} />
          <Route path="/auth/verify" element={<MagicLinkVerify />} />
          <Route path="/accept" element={<AcceptInvite />} />
          <Route path="*" element={<PortalRoot />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
